import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Button, HStack, Text, VStack } from "@chakra-ui/react";

function wsBaseFromApiBase() {
  const api = process.env.REACT_APP_API_BASE_URL || window.location.origin;
  return String(api).replace(/^http/, "ws").replace(/\/$/, "");
}

export default function LiveSupport() {
  const { ticketID } = useParams();
  const [searchParams] = useSearchParams();
  const role = searchParams.get("role") || "customer";
  const room = searchParams.get("room");
  const token = searchParams.get("token");

  const wsUrl = useMemo(() => {
    if (!room) return null;
    const qp = new URLSearchParams();
    if (token) qp.set("token", token);
    if (role) qp.set("role", role);
    const base = wsBaseFromApiBase();
    return `${base}/ws/live-support/${room}/?${qp.toString()}`;
  }, [room, token, role]);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const drawingRef = useRef({ drawing: false, last: null, color: "#ff2d55", width: 4 });

  const [status, setStatus] = useState("Hazırlanıyor...");
  const [error, setError] = useState("");
  const [remoteJoined, setRemoteJoined] = useState(false);

  const canDraw = role === "support";

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    const video = remoteVideoRef.current;
    if (!canvas || !video) return;
    const rect = video.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * dpr));
    canvas.height = Math.max(1, Math.floor(rect.height * dpr));
    canvas.style.width = `${Math.max(1, rect.width)}px`;
    canvas.style.height = `${Math.max(1, rect.height)}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const drawLine = ({ from, to, color, width }) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const fx = from.x * rect.width;
    const fy = from.y * rect.height;
    const tx = to.x * rect.width;
    const ty = to.y * rect.height;
    ctx.strokeStyle = color || "#ff2d55";
    ctx.lineWidth = width || 4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(fx, fy);
    ctx.lineTo(tx, ty);
    ctx.stroke();
  };

  const send = (payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(payload));
  };

  const startOffer = useCallback(async () => {
    const pc = pcRef.current;
    if (!pc) return;
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    send({ kind: "signal", data: { type: "offer", sdp: offer.sdp } });
  }, []);

  const getUserMedia = useCallback(async (constraints) => {
    if (typeof window === "undefined") throw new Error("Browser required");
    if (!window.isSecureContext && window.location.hostname !== "localhost") {
      throw new Error(`Kamera/mikrofon için HTTPS gerekli: ${window.location.origin}`);
    }
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      return navigator.mediaDevices.getUserMedia(constraints);
    }
    const legacy = navigator.getUserMedia || navigator.webkitGetUserMedia || navigator.mozGetUserMedia;
    if (legacy) {
      return new Promise((resolve, reject) => legacy.call(navigator, constraints, resolve, reject));
    }
    throw new Error("Kamera API bulunamadı");
  }, []);

  const getBestMediaStream = useCallback(async () => {
    try {
      return await getUserMedia({ video: true, audio: true });
    } catch (e1) {
      try {
        return await getUserMedia({ video: true, audio: false });
      } catch (e2) {
        return await getUserMedia({ video: false, audio: true });
      }
    }
  }, [getUserMedia]);

  useEffect(() => {
    if (!room) {
      setError("Link eksik: room parametresi bulunamadı.");
      setStatus("Hata");
      return;
    }
    if (!token) {
      setError("Link eksik: token parametresi bulunamadı.");
      setStatus("Hata");
      return;
    }

    let alive = true;
    let resizeTimer = null;
    let pingTimer = null;

    const init = async () => {
      try {
        setStatus("Kamera/mikrofon açılıyor...");
        const localStream = await getBestMediaStream();
        if (!alive) return;
        localStreamRef.current = localStream;
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = localStream;
        }

        setStatus("Bağlantı kuruluyor...");
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;

        const pc = new RTCPeerConnection({
          iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
        });
        pcRef.current = pc;

        localStream.getTracks().forEach((track) => pc.addTrack(track, localStream));

        pc.ontrack = (evt) => {
          const [stream] = evt.streams || [];
          if (remoteVideoRef.current && stream) {
            remoteVideoRef.current.srcObject = stream;
            setRemoteJoined(true);
            setTimeout(() => resizeCanvas(), 50);
          }
        };

        pc.onicecandidate = (evt) => {
          if (!evt.candidate) return;
          send({ kind: "signal", data: { type: "candidate", candidate: evt.candidate } });
        };

        ws.onopen = () => {
          setStatus("Karşı taraf bekleniyor...");
          send({ kind: "ready", role, ticket_id: ticketID });
          if (role === "support") {
            setTimeout(() => {
              startOffer().catch(() => { });
            }, 200);
          }
        };

        ws.onmessage = async (evt) => {
          let msg = null;
          try {
            msg = JSON.parse(evt.data || "{}");
          } catch {
            return;
          }

          if (msg.kind === "error") {
            setError(`Bağlantı yetkisi hatası: ${msg.code || "unknown"}`);
            setStatus("Hata");
            return;
          }

          if (msg.kind === "presence" && msg.event === "join") {
            if (msg.role && msg.role !== role) setStatus("Bağlandı");
          }

          if (msg.kind === "ready") {
            if (role === "support") {
              try {
                if (pc.signalingState === "stable") {
                  await startOffer();
                }
              } catch { }
            }
          }

          if (msg.kind === "signal" && msg.data) {
            const data = msg.data;
            if (data.type === "offer") {
              await pc.setRemoteDescription({ type: "offer", sdp: data.sdp });
              const answer = await pc.createAnswer();
              await pc.setLocalDescription(answer);
              send({ kind: "signal", data: { type: "answer", sdp: answer.sdp } });
              setStatus("Bağlandı");
            } else if (data.type === "answer") {
              await pc.setRemoteDescription({ type: "answer", sdp: data.sdp });
              setStatus("Bağlandı");
            } else if (data.type === "candidate" && data.candidate) {
              try {
                await pc.addIceCandidate(data.candidate);
              } catch { }
            }
          }

          if (msg.kind === "draw") {
            if (msg.action === "clear") {
              clearCanvas();
            }
            if (msg.action === "line") {
              drawLine(msg);
            }
          }
        };

        ws.onerror = () => {
          setStatus("Bağlantı hatası");
        };

        ws.onclose = (ev) => {
          setStatus("Bağlantı kapandı");
          try {
            setError(`WebSocket kapandı. code=${ev?.code}${ev?.reason ? ` reason=${ev.reason}` : ""}`);
          } catch { }
        };

        pingTimer = setInterval(() => {
          try {
            send({ kind: "ping" });
          } catch { }
        }, 15000);

        const onResize = () => {
          if (resizeTimer) clearTimeout(resizeTimer);
          resizeTimer = setTimeout(() => resizeCanvas(), 100);
        };
        window.addEventListener("resize", onResize);
      } catch (e) {
        setError(e?.message || "Kamera/mikrofon erişimi alınamadı.");
        setStatus("Hata");
      }
    };

    init();

    return () => {
      alive = false;
      try {
        window.removeEventListener("resize", resizeCanvas);
      } catch { }
      try {
        if (pingTimer) clearInterval(pingTimer);
      } catch { }
      try {
        if (wsRef.current) wsRef.current.close();
      } catch { }
      try {
        if (pcRef.current) pcRef.current.close();
      } catch { }
      try {
        const s = localStreamRef.current;
        if (s) s.getTracks().forEach((t) => t.stop());
      } catch { }
    };
  }, [room, token, wsUrl, role, ticketID, startOffer, getBestMediaStream]);

  useEffect(() => {
    const v = remoteVideoRef.current;
    if (!v) return;
    const onLoaded = () => resizeCanvas();
    v.addEventListener("loadedmetadata", onLoaded);
    return () => v.removeEventListener("loadedmetadata", onLoaded);
  }, [remoteJoined]);

  const getPoint = (evt) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;
    return { x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) };
  };

  const onPointerDown = (evt) => {
    if (!canDraw) return;
    const p = getPoint(evt);
    if (!p) return;
    drawingRef.current.drawing = true;
    drawingRef.current.last = p;
  };

  const onPointerMove = (evt) => {
    if (!canDraw) return;
    if (!drawingRef.current.drawing) return;
    const p = getPoint(evt);
    const last = drawingRef.current.last;
    if (!p || !last) return;
    const seg = {
      kind: "draw",
      action: "line",
      from: last,
      to: p,
      color: drawingRef.current.color,
      width: drawingRef.current.width,
    };
    drawLine(seg);
    send(seg);
    drawingRef.current.last = p;
  };

  const onPointerUp = () => {
    if (!canDraw) return;
    drawingRef.current.drawing = false;
    drawingRef.current.last = null;
  };

  return (
    <Box p={4}>
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontSize="lg" fontWeight="700">
            Canlı Destek
          </Text>
          <Text fontSize="sm" color="gray.600">
            Ticket #{ticketID} • Durum: {status}
          </Text>
          {error ? (
            <Text mt={2} fontSize="sm" color="red.600">
              {error}
            </Text>
          ) : null}
        </Box>

        <HStack align="start" spacing={4} flexWrap="wrap">
          <Box flex="1" minW={{ base: "100%", md: "320px" }}>
            <Text fontSize="sm" mb={2} fontWeight="600">
              Senin Kameran
            </Text>
            <Box bg="black" borderRadius="md" overflow="hidden">
              <video ref={localVideoRef} autoPlay muted playsInline style={{ width: "100%", height: "auto" }} />
            </Box>
          </Box>

          <Box flex="2" minW={{ base: "100%", md: "520px" }}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="600">
                Karşı Taraf
              </Text>
              {canDraw ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    clearCanvas();
                    send({ kind: "draw", action: "clear" });
                  }}
                  isDisabled={!remoteJoined}
                >
                  Temizle
                </Button>
              ) : null}
            </HStack>

            <Box position="relative" bg="black" borderRadius="md" overflow="hidden">
              <video ref={remoteVideoRef} autoPlay playsInline style={{ width: "100%", height: "auto" }} />
              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  pointerEvents: canDraw ? "auto" : "none",
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                onPointerLeave={onPointerUp}
              />
            </Box>
            {canDraw ? (
              <Text mt={2} fontSize="xs" color="gray.600">
                Çizmek için karşı taraf videosunun üstüne basılı tutup sürükle.
              </Text>
            ) : null}
          </Box>
        </HStack>
      </VStack>
    </Box>
  );
}
